import { Spin } from 'antd';
import { Suspense, lazy } from 'react';

import { useParams } from '@suntex.core/nav';
import {
  $KnowAndShowCategory,
  $KnowAndShowLevel,
  $ModuleInfo,
  KnowAndShowCategory,
  KnowAndShowLevel,
  ModuleInfo,
} from '@suntex.fim/enums';
import { getFlags } from '@suntex.fim/flags';
import { useModuleById } from '@suntex.fim/framework';

import { ModuleLayout } from '../components/ModuleLayout';

import { IMAGE_DIRECTORY, SOUND_DIRECTORY, useModuleViewProps } from './shared';

const KnowAndShowCanvasView = lazy(() =>
  import('@suntex.modules/word-problems').then((m) => ({
    default: m.WordProblemsView,
  })),
);

const KnowAndShowReactView = lazy(() =>
  import('@suntex.modules/know-and-show').then((m) => ({
    default: m.KnowAndShowReactView,
  })),
);

const WORD_PROBLEMS_IMAGE_DIRECTORY = `${IMAGE_DIRECTORY}know-and-show/`;

export function KnowAndShow() {
  const params = useParams<{ level: string; category: string }>();
  const viewProps = useModuleViewProps();

  const level = $KnowAndShowLevel.from(params.level!);
  const category = $KnowAndShowCategory.from(params.category!);
  const moduleId = knowAndShowModuleId(level, category);
  const module = useModuleById(moduleId);

  if (category > $KnowAndShowLevel.describe(level).categoryCount) {
    return <div>Invalid category for level {level}</div>;
  }

  if (!module) {
    return <Spin />;
  }

  const kasProps = {
    ...viewProps,
    module,
    moduleId,
    level,
    category,
    soundDirectory: SOUND_DIRECTORY,
    imageDirectory: WORD_PROBLEMS_IMAGE_DIRECTORY,
  };

  return (
    <ModuleLayout moduleId={moduleId}>
      <Suspense fallback={<Spin />}>
        {getFlags().modules.wordProblems.defaultToReactView ? (
          <KnowAndShowReactView {...kasProps} />
        ) : (
          <KnowAndShowCanvasView {...kasProps} />
        )}
      </Suspense>
    </ModuleLayout>
  );
}

function ksBase(level: KnowAndShowLevel): ModuleInfo {
  switch (level) {
    case KnowAndShowLevel.KS1:
      return ModuleInfo.ks_1_1;
    case KnowAndShowLevel.KS2:
      return ModuleInfo.ks_2_1;
    case KnowAndShowLevel.KS3:
      return ModuleInfo.ks_3_1;
    case KnowAndShowLevel.KS4:
      return ModuleInfo.ks_4_1;
    case KnowAndShowLevel.KS5:
      return ModuleInfo.ks_5_1;
    case KnowAndShowLevel.KS6:
      return ModuleInfo.ks_6_1;
    case KnowAndShowLevel.KS7:
      return ModuleInfo.ks_7_1;
    case KnowAndShowLevel.KS8:
      return ModuleInfo.ks_8_1;
    default:
      return $KnowAndShowLevel.throwInvalid(level);
  }
}

function knowAndShowModuleId(
  level: KnowAndShowLevel,
  category: KnowAndShowCategory,
): ModuleInfo {
  return $ModuleInfo.from(ksBase(level) + category - 1);
}
